import { useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { MdAdd } from 'react-icons/md';
import { Button } from '@Components/Button';
import { Design, Size } from '@Components/designConstants';
import { IconButton } from '@Components/IconButton';
import { Spinner } from '@Components/Spinner/Spinner';
import { useSetError } from '@src/redux/modals';
import { AttributeType, AttributeTypeType } from './streaming-networks-constants';
import {
  AttributeSchema,
  NewAttributeSchemaParams,
  saveAttributeSchema,
  useAttributeSchema
} from './use-streaming-networks';

const ATTRIBUTE_TYPE_ORDER: AttributeTypeType[] = [
  AttributeType.MULTI_SELECT,
  AttributeType.BOOLEAN,
  AttributeType.NUMBER,
  AttributeType.TEXT
];

const ATTRIBUTE_TYPE_LABELS: Record<AttributeTypeType, string> = {
  [AttributeType.MULTI_SELECT]: 'Multi-Select',
  [AttributeType.BOOLEAN]: 'True/False',
  [AttributeType.NUMBER]: 'Number',
  [AttributeType.TEXT]: 'Free Text'
};

interface DraftAttribute {
  name: string;
  type: AttributeTypeType;
  options: string[];
}

interface AttributeSchemaModalProps {
  show: boolean;
  onHide: () => void;
}

export const AttributeSchemaModal = ({ show, onHide }: AttributeSchemaModalProps) => {
  const setError = useSetError();
  const schemaQuery = useAttributeSchema();
  const schema = schemaQuery.data ?? {};

  const [draftAttributes, setDraftAttributes] = useState<Record<string, DraftAttribute>>({});
  const [newOptions, setNewOptions] = useState<Record<string, string[]>>({});
  const [saving, setSaving] = useState(false);

  const hasPendingChanges =
    Object.keys(draftAttributes).length > 0 || Object.keys(newOptions).length > 0;

  const addDraftAttribute = (type: AttributeTypeType) => {
    const id = crypto.randomUUID();
    setDraftAttributes(prev => ({ ...prev, [id]: { name: '', type, options: [] } }));
  };

  const updateDraftName = (id: string, name: string) =>
    setDraftAttributes(prev => ({ ...prev, [id]: { ...prev[id], name } }));

  const addDraftOption = (id: string) =>
    setDraftAttributes(prev => ({
      ...prev,
      [id]: { ...prev[id], options: [...prev[id].options, ''] }
    }));

  const updateDraftOption = (id: string, index: number, value: string) =>
    setDraftAttributes(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        options: prev[id].options.map((option, i) => (i === index ? value : option))
      }
    }));

  const addOptionToExisting = (id: string) =>
    setNewOptions(prev => ({ ...prev, [id]: [...(prev[id] ?? []), ''] }));

  const updateExistingOption = (id: string, index: number, value: string) =>
    setNewOptions(prev => ({
      ...prev,
      [id]: (prev[id] ?? []).map((option, i) => (i === index ? value : option))
    }));

  const discardChanges = () => {
    setDraftAttributes({});
    setNewOptions({});
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const newAttributes: NewAttributeSchemaParams['newAttributes'] = {};
      Object.entries(draftAttributes).forEach(([id, attribute]) => {
        newAttributes[id] = {
          name: attribute.name.trim(),
          type: attribute.type,
          options: attribute.options,
          new: true
        };
      });
      await saveAttributeSchema({ newAttributes, newOptions });
      discardChanges();
      await schemaQuery.refetch();
    } catch (e) {
      setError({ message: `Couldn't save attribute schema: ${e.message}`, reportError: e });
    }
    setSaving(false);
  };

  const handleHide = () => {
    onHide();
    discardChanges();
  };

  return (
    <Modal
      show={show}
      onHide={handleHide}
      size='xl'
      centered
      scrollable
      className='sn-attribute-schema'
    >
      <Modal.Header closeButton>
        <Modal.Title>Attribute Schema</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <AttributeSchemaBody
          isLoading={schemaQuery.isLoading}
          schema={schema}
          draftAttributes={draftAttributes}
          newOptions={newOptions}
          onAddDraftAttribute={addDraftAttribute}
          onDraftNameChange={updateDraftName}
          onAddDraftOption={addDraftOption}
          onDraftOptionChange={updateDraftOption}
          onAddExistingOption={addOptionToExisting}
          onExistingOptionChange={updateExistingOption}
        />
      </Modal.Body>
      <Modal.Footer>
        {hasPendingChanges && (
          <>
            <Button
              size={Size.LG}
              design={Design.NEUTRAL}
              disabled={saving}
              onClick={discardChanges}
            >
              Discard
            </Button>
            <Button size={Size.LG} loading={saving} onClick={handleSave}>
              Save
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

interface AttributeSchemaBodyProps {
  isLoading: boolean;
  schema: Record<string, AttributeSchema>;
  draftAttributes: Record<string, DraftAttribute>;
  newOptions: Record<string, string[]>;
  onAddDraftAttribute: (type: AttributeTypeType) => void;
  onDraftNameChange: (id: string, name: string) => void;
  onAddDraftOption: (id: string) => void;
  onDraftOptionChange: (id: string, index: number, value: string) => void;
  onAddExistingOption: (id: string) => void;
  onExistingOptionChange: (id: string, index: number, value: string) => void;
}

const AttributeSchemaBody = ({
  isLoading,
  schema,
  draftAttributes,
  newOptions,
  onAddDraftAttribute,
  onDraftNameChange,
  onAddDraftOption,
  onDraftOptionChange,
  onAddExistingOption,
  onExistingOptionChange
}: AttributeSchemaBodyProps) => {
  if (isLoading) return <Spinner />;

  return (
    <div className='sn-attribute-schema-body'>
      <div className='sn-attribute-schema-new-buttons'>
        {ATTRIBUTE_TYPE_ORDER.map(type => (
          <Button
            key={type}
            size={Size.LG}
            leadingElement={<MdAdd />}
            onClick={() => onAddDraftAttribute(type)}
          >
            {ATTRIBUTE_TYPE_LABELS[type]}
          </Button>
        ))}
      </div>
      <div className='sn-attribute-schema-grid'>
        {Object.values(schema).map(attribute => (
          <ExistingAttributeCard
            key={attribute.id}
            attribute={attribute}
            draftOptions={newOptions[String(attribute.id)] ?? []}
            onAddOption={() => onAddExistingOption(String(attribute.id))}
            onOptionChange={(index, value) =>
              onExistingOptionChange(String(attribute.id), index, value)
            }
          />
        ))}
        {Object.entries(draftAttributes).map(([id, attribute]) => (
          <DraftAttributeCard
            key={id}
            attribute={attribute}
            onNameChange={name => onDraftNameChange(id, name)}
            onAddOption={() => onAddDraftOption(id)}
            onOptionChange={(index, value) => onDraftOptionChange(id, index, value)}
          />
        ))}
      </div>
    </div>
  );
};

interface OptionsEditorProps {
  draftOptions: string[];
  onAddOption: () => void;
  onOptionChange: (index: number, value: string) => void;
}

const OptionsEditor = ({ draftOptions, onAddOption, onOptionChange }: OptionsEditorProps) => (
  <div className='sn-attribute-schema-options'>
    {draftOptions.map((option, index) => (
      <Form.Control
        key={index}
        size='sm'
        placeholder='New option'
        value={option}
        onChange={event => onOptionChange(index, event.target.value)}
      />
    ))}
    <IconButton size={Size.MD} onClick={onAddOption} aria-label='Add option'>
      <MdAdd />
    </IconButton>
  </div>
);

interface ExistingAttributeCardProps {
  attribute: AttributeSchema;
  draftOptions: string[];
  onAddOption: () => void;
  onOptionChange: (index: number, value: string) => void;
}

const ExistingAttributeCard = ({
  attribute,
  draftOptions,
  onAddOption,
  onOptionChange
}: ExistingAttributeCardProps) => {
  const existingOptions = Array.isArray(attribute.value) ? attribute.value : [];

  return (
    <div className='sn-attribute-schema-card'>
      <div className='sn-attribute-schema-name'>{attribute.attribute}</div>
      {attribute.type === AttributeType.MULTI_SELECT ? (
        <>
          <div className='sn-attribute-schema-pills'>
            {existingOptions.map(option => (
              <span className='sn-attribute-schema-pill' key={option}>
                {option}
              </span>
            ))}
          </div>
          <OptionsEditor
            draftOptions={draftOptions}
            onAddOption={onAddOption}
            onOptionChange={onOptionChange}
          />
        </>
      ) : (
        <div className='sn-attribute-schema-type-label'>
          {ATTRIBUTE_TYPE_LABELS[attribute.type]}
        </div>
      )}
    </div>
  );
};

interface DraftAttributeCardProps {
  attribute: DraftAttribute;
  onNameChange: (name: string) => void;
  onAddOption: () => void;
  onOptionChange: (index: number, value: string) => void;
}

const DraftAttributeCard = ({
  attribute,
  onNameChange,
  onAddOption,
  onOptionChange
}: DraftAttributeCardProps) => (
  <div className='sn-attribute-schema-card'>
    <Form.Control
      size='sm'
      placeholder='Attribute name'
      value={attribute.name}
      onChange={event => onNameChange(event.target.value)}
    />
    {attribute.type === AttributeType.MULTI_SELECT ? (
      <OptionsEditor
        draftOptions={attribute.options}
        onAddOption={onAddOption}
        onOptionChange={onOptionChange}
      />
    ) : (
      <div className='sn-attribute-schema-type-label'>{ATTRIBUTE_TYPE_LABELS[attribute.type]}</div>
    )}
  </div>
);
